import React, { useState } from 'react';
import { Layers, Download, Info, DollarSign, BarChart2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения графика платежей по валютной ипотеке
 */
const PaymentSchedule = ({ schedule, results, formData, showFullSchedule, setShowFullSchedule }) => {
  const [activeTab, setActiveTab] = useState('general');
  
  if (!schedule || schedule.length === 0 || !results) {
    return null;
  }

  // Для отображения только первого года, если не показан полный график
  const displaySchedule = showFullSchedule ? schedule : schedule.slice(0, 12);

  // Начальные итоговые суммы
  const totals = {
    payment: schedule.reduce((sum, item) => sum + item.payment, 0),
    principal: schedule.reduce((sum, item) => sum + item.principalPayment, 0),
    interest: schedule.reduce((sum, item) => sum + item.interestPayment, 0),
    earlyRepayment: schedule.reduce((sum, item) => sum + (item.earlyRepayment || 0), 0),
    paymentRUB: schedule.reduce((sum, item) => sum + item.paymentRUB, 0),
    totalPaymentRUB: schedule.reduce((sum, item) => sum + item.totalPaymentRUB, 0)
  };

  // Получение символа валюты
  const getCurrencySymbol = () => {
    switch (formData.currency) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'CHF': return 'Fr';
      case 'JPY': case 'CNY': return '¥';
      default: return formData.currency;
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Layers className="w-5 h-5 mr-2 text-indigo-500" />
          График платежей ({schedule.length} мес.)
        </h3>

        {/* Кнопка переключения полного/краткого графика */}
        {schedule.length > 12 && (
          <Button
            size="sm"
            onClick={() => setShowFullSchedule(prev => !prev)}
            variant="outline"
            aria-expanded={showFullSchedule}
          >
            {showFullSchedule ? 'Показать краткий график' : `Показать все ${schedule.length} мес.`}
          </Button>
        )}
      </div>

      {/* Вкладки для разных типов отображения */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('general')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'general'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Общий график
        </button>
        <button
          onClick={() => setActiveTab('currency')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'currency'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Курс валюты
        </button>
        <button
          onClick={() => setActiveTab('detailed')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'detailed'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Все платежи
        </button>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>{showFullSchedule 
            ? "Полный график всех платежей по ипотеке с прогнозом изменения курса валюты. Прокрутите таблицу вправо для просмотра всех данных." 
            : `Показаны первые ${displaySchedule.length} месяцев графика платежей. Нажмите "Показать все месяцы" для просмотра полного графика.`
          }</p>
        </div>
      </div>

      {/* Контейнер таблицы с прокруткой */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-[600px] payment-schedule-table">
        <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[800px]">
          <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
            <tr>
              <th scope="col" className="px-3 py-2 font-semibold text-center">№</th>
              <th scope="col" className="px-3 py-2 font-semibold">Дата</th>

              {activeTab === 'general' && (
                <>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">{`Платеж (${formData.currency})`}</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Осн. долг</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Проценты</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Платеж (₽)</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">{`Остаток (${formData.currency})`}</th>
                </>
              )}

              {activeTab === 'currency' && (
                <>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Курс {`${formData.currency}/₽`}</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">{`Платеж (${formData.currency})`}</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Платеж (₽)</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Изм. курса</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Изм. платежа (₽)</th>
                </>
              )}

              {activeTab === 'detailed' && (
                <>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">{`Платеж (${formData.currency})`}</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Осн. долг</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Проценты</th>
                  {results.totalEarlyRepayment > 0 && (
                    <th scope="col" className="px-3 py-2 font-semibold text-right">Досрочное погашение</th>
                  )}
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Курс валюты</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Платеж (₽)</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">{`Остаток (${formData.currency})`}</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {displaySchedule.map((item, index) => {
              // Расчет процентного изменения курса относительно предыдущего месяца
              const prevRate = index > 0 ? displaySchedule[index - 1].exchangeRate : results.initialExchangeRate;
              const rateChangePercent = ((item.exchangeRate - prevRate) / prevRate) * 100;
              
              // Расчет изменения платежа в рублях из-за курса
              const prevPaymentRUB = index > 0 ? displaySchedule[index - 1].paymentRUB : item.payment * results.initialExchangeRate;
              const paymentChangeRUB = item.paymentRUB - prevPaymentRUB;
                
              return (
                <tr
                  key={item.month}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100
                    ${item.earlyRepayment > 0 ? 'bg-green-50 dark:bg-green-900/20' : ''}
                    ${Math.abs(rateChangePercent) > 3 ? 'bg-amber-50 dark:bg-amber-900/20' : ''}`}
                >
                  <td className="px-3 py-1.5 whitespace-nowrap text-center font-medium">{item.month}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    {new Date(item.date).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </td>

                  {activeTab === 'general' && (
                    <>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium">{formatNumber(item.payment, 2)}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right">{formatNumber(item.principalPayment, 2)}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right text-red-600 dark:text-red-400">{formatNumber(item.interestPayment, 2)}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium">{formatNumber(item.paymentRUB, 0)} ₽</td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium text-gray-800 dark:text-gray-100">
                        {formatNumber(item.remainingBalance, 2)}
                      </td>
                    </>
                  )}

                  {activeTab === 'currency' && (
                    <>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium">
                        {formatNumber(item.exchangeRate, 2)}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right">
                        {formatNumber(item.payment, 2)}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium">
                        {formatNumber(item.paymentRUB, 0)} ₽
                      </td>
                      <td className={`px-3 py-1.5 whitespace-nowrap text-right ${
                        rateChangePercent > 0 
                          ? 'text-red-600 dark:text-red-400' 
                          : rateChangePercent < 0 
                            ? 'text-green-600 dark:text-green-400' 
                            : ''
                      }`}>
                        {rateChangePercent > 0 ? '+' : ''}{formatNumber(rateChangePercent, 2)}%
                      </td>
                      <td className={`px-3 py-1.5 whitespace-nowrap text-right ${
                        paymentChangeRUB > 0 
                          ? 'text-red-600 dark:text-red-400' 
                          : paymentChangeRUB < 0 
                            ? 'text-green-600 dark:text-green-400' 
                            : ''
                      }`}>
                        {paymentChangeRUB > 0 ? '+' : ''}{formatNumber(paymentChangeRUB, 0)} ₽
                      </td>
                    </>
                  )}

                  {activeTab === 'detailed' && (
                    <>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium">{formatNumber(item.payment, 2)}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right">{formatNumber(item.principalPayment, 2)}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right text-red-600 dark:text-red-400">{formatNumber(item.interestPayment, 2)}</td>
                      {results.totalEarlyRepayment > 0 && (
                        <td className={`px-3 py-1.5 whitespace-nowrap text-right ${item.earlyRepayment > 0 ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-gray-400 dark:text-gray-500'}`}>
                          {item.earlyRepayment > 0 ? formatNumber(item.earlyRepayment, 2) : '-'}
                        </td>
                      )}
                      <td className="px-3 py-1.5 whitespace-nowrap text-right">
                        {formatNumber(item.exchangeRate, 2)}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium">
                        {formatNumber(item.paymentRUB, 0)} ₽
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium text-gray-800 dark:text-gray-100">
                        {formatNumber(item.remainingBalance, 2)}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}

            {/* Итоговая строка */}
            <tr className="bg-gray-50 dark:bg-gray-700 font-semibold">
              <td className="px-3 py-2 whitespace-nowrap text-center" colSpan="2">Итого:</td>

              {activeTab === 'general' && (
                <>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatNumber(totals.payment, 2)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatNumber(totals.principal, 2)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-red-600 dark:text-red-400">{formatNumber(totals.interest, 2)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatNumber(totals.paymentRUB, 0)} ₽</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">-</td>
                </>
              )}

              {activeTab === 'currency' && (
                <>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatNumber(results.averageExchangeRate, 2)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatNumber(totals.payment, 2)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatNumber(totals.paymentRUB, 0)} ₽</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">-</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">-</td>
                </>
              )}

              {activeTab === 'detailed' && (
                <>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatNumber(totals.payment, 2)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatNumber(totals.principal, 2)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-red-600 dark:text-red-400">{formatNumber(totals.interest, 2)}</td>
                  {results.totalEarlyRepayment > 0 && (
                    <td className="px-3 py-2 whitespace-nowrap text-right text-green-600 dark:text-green-400">{formatNumber(totals.earlyRepayment, 2)}</td>
                  )}
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatNumber(results.averageExchangeRate, 2)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatNumber(totals.paymentRUB, 0)} ₽</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">-</td>
                </>
              )}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Кнопка показа полного графика (если он скрыт и больше предпросмотра) */}
      {!showFullSchedule && schedule.length > 12 && (
        <div className="text-center mt-4">
          <Button
            onClick={() => setShowFullSchedule(true)}
            icon={<Layers className="w-4 h-4" />}
            variant="link"
          >
            Показать все {schedule.length} платежей
          </Button>
        </div>
      )}
    </div>
  );
};

export default PaymentSchedule;